import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import Entry from "@/pages/Entry";
import NfcEntry from "@/pages/NfcEntry";
import CavePicker from "@/pages/CavePicker";
import Caves from "@/pages/Caves";
import LocationContents from "@/pages/LocationContents";
import { LegalPage } from "@/components/LegalPage";
import { CookieNotice } from "@/components/CookieNotice";
import { PRIVACY, COOKIES } from "@/lib/legal";
import Inventory from "@/pages/Inventory";
import WineDetail from "@/pages/WineDetail";
import Scan from "@/pages/Scan";
import History from "@/pages/History";
import Locations from "@/pages/Locations";
import CellarSetup from "@/pages/CellarSetup";
import Users from "@/pages/Users";
import Sales from "@/pages/Sales";
import Value from "@/pages/Value";
import Reports from "@/pages/Reports";
import Boxes from "@/pages/Boxes";
import BoxContents from "@/pages/BoxContents";
import PrintLabels from "@/pages/PrintLabels";

const QuickCode = () => {
  const { code } = useParams();
  return <Navigate to={`/scan?code=${encodeURIComponent(code.toUpperCase())}`} replace />;
};

const Protected = ({ children, ownerOnly, needCave = true }) => {
  const { user } = useAuth();
  const here = useLocation();
  if (user === null) {
    return <div className="min-h-screen grid place-items-center text-[#A89F91] font-mono text-sm" data-testid="auth-loading">Opening the cellar…</div>;
  }
  if (!user || (needCave && !user.cave_id)) {
    if (here.pathname !== "/") sessionStorage.setItem("return_to", here.pathname + here.search);
  }
  if (!user) return <Navigate to="/login" replace />;
  if (needCave && !user.cave_id) return <Navigate to="/choose-cave" replace />;
  if (ownerOnly && user.role !== "owner") return <Navigate to="/" replace />;
  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Entry />} />
          <Route path="/privacy" element={<LegalPage title="Política de Privacidade" sections={PRIVACY} testId="privacy-page" />} />
          <Route path="/cookies" element={<LegalPage title="Política de Cookies" sections={COOKIES} testId="cookies-page" />} />
          <Route path="/nfc/:id" element={<NfcEntry />} />
          <Route path="/choose-cave" element={<Protected needCave={false}><CavePicker /></Protected>} />
          <Route path="/print" element={<Protected><PrintLabels /></Protected>} />
          <Route element={<Protected><Layout /></Protected>}>
            <Route path="/" element={<Inventory />} />
            <Route path="/wines/:id" element={<WineDetail />} />
            <Route path="/scan" element={<Scan />} />
            <Route path="/history" element={<History />} />
            <Route path="/sales" element={<Sales />} />
            <Route path="/value" element={<Value />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/boxes" element={<Boxes />} />
            <Route path="/b/:code" element={<BoxContents />} />
            <Route path="/q/:code" element={<QuickCode />} />
            <Route path="/Q/:code" element={<QuickCode />} />
            <Route path="/locations" element={<Locations />} />
            <Route path="/locations/:id" element={<LocationContents />} />
            <Route path="/setup" element={<Protected ownerOnly><CellarSetup /></Protected>} />
            <Route path="/caves" element={<Protected ownerOnly><Caves /></Protected>} />
            <Route path="/users" element={<Protected ownerOnly><Users /></Protected>} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <CookieNotice />
      </BrowserRouter>
      <Toaster theme="dark" position="top-center" richColors />
    </AuthProvider>
  );
}

export default App;
